import { useState } from 'react';
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Pencil,
  Eye,
  EyeOff,
  HardDrive,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { LoadingPage } from '@/components/ui/loading';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { formatRelativeTime } from '@/lib/utils';
import { useDeviceDatabase } from '../hooks/useDeviceDatabase';
import { ProvisionedMeterFormModal } from './ProvisionedMeterFormModal';
import type { ProvisionedMeter } from '@/types/api';

function MaskedAppKey({ value }: { value: string }) {
  const [revealed, setRevealed] = useState(false);

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="font-mono text-sm">
        {revealed ? value : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
      </span>
      <button
        type="button"
        onClick={() => setRevealed(v => !v)}
        className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        aria-label={revealed ? 'Hide AppKey' : 'Reveal AppKey'}
      >
        {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
    </span>
  );
}

function MeterTableRow({
  meter,
  onEdit,
  onDelete,
}: {
  meter: ProvisionedMeter;
  onEdit: (meter: ProvisionedMeter) => void;
  onDelete: (meter: ProvisionedMeter) => void;
}) {
  return (
    <TableRow>
      <TableCell className="font-mono whitespace-nowrap">
        {meter.serialNumber}
      </TableCell>
      <TableCell className="font-mono whitespace-nowrap">
        {meter.devEui}
      </TableCell>
      <TableCell className="font-mono whitespace-nowrap">
        {meter.joinEui}
      </TableCell>
      <TableCell>
        <MaskedAppKey value={meter.appKey} />
      </TableCell>
      <TableCell className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {formatRelativeTime(meter.provisionedAt)}
      </TableCell>
      <TableCell className="text-right">
        <div className="inline-flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(meter)}
            title="Edit meter"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-error-600 hover:text-error-700"
            onClick={() => onDelete(meter)}
            title="Delete meter"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function DeviceDatabasePage() {
  const {
    meters,
    loading,
    totalCount,
    totalPages,
    currentPage,
    searchInput,
    setSearchInput,
    handlePageChange,
    isModalOpen,
    setIsModalOpen,
    editingMeter,
    handleAdd,
    handleEdit,
    handleSave,
    deletingMeter,
    setDeletingMeter,
    isDeleting,
    handleDeleteConfirm,
  } = useDeviceDatabase();

  if (loading) return <LoadingPage />;

  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by serial or DevEUI..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="ml-auto">
              <Button onClick={handleAdd}>
                <Plus className="w-5 h-5" />
                Add Meter
              </Button>
            </div>
          </div>

          {/* Table */}
          {meters.length === 0 ? (
            <Card variant="elevated">
              <EmptyState
                icon={<HardDrive className="w-16 h-16" />}
                title="No provisioned meters found"
                description={searchInput
                  ? 'Try adjusting your search terms.'
                  : 'Add your first provisioned meter to get started.'}
                action={!searchInput ? { label: 'Add Meter', onClick: handleAdd } : undefined}
              />
            </Card>
          ) : (
            <Card variant="elevated" padding="none">
              <Table>
                <TableHeader>
                  <TableRow className="whitespace-nowrap">
                    <TableHead>Serial Number</TableHead>
                    <TableHead>DevEUI</TableHead>
                    <TableHead>JoinEUI</TableHead>
                    <TableHead>AppKey</TableHead>
                    <TableHead>Provisioned</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {meters.map((meter) => (
                    <MeterTableRow
                      key={meter.id}
                      meter={meter}
                      onEdit={handleEdit}
                      onDelete={setDeletingMeter}
                    />
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex items-center justify-center sm:justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="hidden sm:block text-sm text-gray-600 dark:text-gray-400">
                    {totalCount} total meters
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {isModalOpen && (
        <ProvisionedMeterFormModal
          meter={editingMeter}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
        />
      )}

      <ConfirmDialog
        isOpen={!!deletingMeter}
        title="Delete Provisioned Meter"
        description={`Are you sure you want to delete meter "${deletingMeter?.serialNumber}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingMeter(null)}
        isLoading={isDeleting}
      />
    </>
  );
}
